var { useRef, useCallback, useEffect } = require('react');

var MASTER_VOLUME = 0.25;

/**
 * Play a slide whistle going up - classic cartoon ascending sound
 * @param {AudioContext} ctx
 * @param {GainNode} masterGain
 */
function playSlideWhistleUp(ctx, masterGain) {
  var startTime = ctx.currentTime;
  var duration = 0.8;

  var osc = ctx.createOscillator();
  var gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(300, startTime);
  osc.frequency.exponentialRampToValueAtTime(2400, startTime + duration);

  gain.gain.setValueAtTime(0.5, startTime);
  gain.gain.setValueAtTime(0.5, startTime + duration - 0.05);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);

  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(startTime);
  osc.stop(startTime + duration);

  // Add a boing at the end
  playBoing(ctx, masterGain, startTime + duration - 0.05);
}

/**
 * Play a cartoon boing/sproing sound
 * @param {AudioContext} ctx
 * @param {GainNode} masterGain
 * @param {number} [offset] - Optional start time offset
 */
function playBoing(ctx, masterGain, offset) {
  var startTime = offset || ctx.currentTime;
  var duration = 0.6;

  var osc = ctx.createOscillator();
  var gain = ctx.createGain();

  osc.type = 'sine';
  // Rapid pitch oscillation for sproing effect
  osc.frequency.setValueAtTime(600, startTime);
  osc.frequency.exponentialRampToValueAtTime(150, startTime + 0.15);
  osc.frequency.exponentialRampToValueAtTime(400, startTime + 0.25);
  osc.frequency.exponentialRampToValueAtTime(100, startTime + 0.4);
  osc.frequency.exponentialRampToValueAtTime(200, startTime + duration);

  gain.gain.setValueAtTime(0.6, startTime);
  gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(startTime);
  osc.stop(startTime + duration);

  // Add a second oscillator for richness
  var osc2 = ctx.createOscillator();
  var gain2 = ctx.createGain();

  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(800, startTime);
  osc2.frequency.exponentialRampToValueAtTime(200, startTime + 0.15);
  osc2.frequency.exponentialRampToValueAtTime(500, startTime + 0.25);
  osc2.frequency.exponentialRampToValueAtTime(120, startTime + duration);

  gain2.gain.setValueAtTime(0.3, startTime);
  gain2.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

  osc2.connect(gain2);
  gain2.connect(masterGain);
  osc2.start(startTime);
  osc2.stop(startTime + duration);
}

/**
 * Play a whoopee cushion / fart sound using filtered noise
 * @param {AudioContext} ctx
 * @param {GainNode} masterGain
 */
function playWhoopeeCushion(ctx, masterGain) {
  var startTime = ctx.currentTime;
  var duration = 1.2;
  var sampleRate = ctx.sampleRate;
  var bufferSize = Math.floor(sampleRate * duration);
  var buffer = ctx.createBuffer(1, bufferSize, sampleRate);
  var data = buffer.getChannelData(0);

  // Generate noise with low-frequency oscillation for the "flapping" effect
  for (var i = 0; i < bufferSize; i++) {
    var t = i / sampleRate;
    var noise = Math.random() * 2 - 1;
    // Modulate with low frequency for the vibrating/flapping sound
    var vibrato = Math.sin(t * Math.PI * 2 * (40 + t * 30));
    // Pitch descends over time
    var buzz = Math.sin(t * Math.PI * 2 * (120 - t * 50));
    data[i] = (noise * 0.3 + vibrato * 0.4 + buzz * 0.3) * (1 - t / duration);
  }

  var source = ctx.createBufferSource();
  source.buffer = buffer;

  // Bandpass filter for that classic muffled quality
  var filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(200, startTime);
  filter.frequency.linearRampToValueAtTime(100, startTime + duration);
  filter.Q.setValueAtTime(2, startTime);

  var gain = ctx.createGain();
  gain.gain.setValueAtTime(0.8, startTime);
  gain.gain.setValueAtTime(0.8, startTime + duration * 0.3);
  gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(masterGain);
  source.start(startTime);
  source.stop(startTime + duration);
}

/**
 * Play just a cartoon boing sound (standalone version)
 * @param {AudioContext} ctx
 * @param {GainNode} masterGain
 */
function playCartoonBoing(ctx, masterGain) {
  playBoing(ctx, masterGain);

  // Add a second boing for extra comedy
  var startTime = ctx.currentTime + 0.35;
  var osc = ctx.createOscillator();
  var gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(800, startTime);
  osc.frequency.exponentialRampToValueAtTime(200, startTime + 0.1);
  osc.frequency.exponentialRampToValueAtTime(500, startTime + 0.2);
  osc.frequency.exponentialRampToValueAtTime(80, startTime + 0.4);

  gain.gain.setValueAtTime(0.4, startTime);
  gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4);

  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(startTime);
  osc.stop(startTime + 0.4);
}

/**
 * Play a slide whistle going up (standalone, no boing)
 * @param {AudioContext} ctx
 * @param {GainNode} masterGain
 */
function playSlideWhistleOnly(ctx, masterGain) {
  var startTime = ctx.currentTime;
  var duration = 1.0;

  var osc = ctx.createOscillator();
  var gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(250, startTime);
  osc.frequency.exponentialRampToValueAtTime(1800, startTime + duration * 0.7);
  // Little wobble at the top
  osc.frequency.setValueAtTime(1800, startTime + duration * 0.7);
  osc.frequency.linearRampToValueAtTime(1600, startTime + duration * 0.8);
  osc.frequency.linearRampToValueAtTime(2000, startTime + duration * 0.9);
  osc.frequency.linearRampToValueAtTime(1400, startTime + duration);

  gain.gain.setValueAtTime(0.5, startTime);
  gain.gain.setValueAtTime(0.5, startTime + duration - 0.1);
  gain.gain.linearRampToValueAtTime(0, startTime + duration);

  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(startTime);
  osc.stop(startTime + duration);

  // Add breathy overtone
  var osc2 = ctx.createOscillator();
  var gain2 = ctx.createGain();
  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(500, startTime);
  osc2.frequency.exponentialRampToValueAtTime(3600, startTime + duration * 0.7);
  osc2.frequency.linearRampToValueAtTime(2800, startTime + duration);

  gain2.gain.setValueAtTime(0.15, startTime);
  gain2.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

  osc2.connect(gain2);
  gain2.connect(masterGain);
  osc2.start(startTime);
  osc2.stop(startTime + duration);
}

/** All available sound functions */
var SOUND_FUNCTIONS = [
  playSlideWhistleUp,
  playWhoopeeCushion,
  playCartoonBoing,
  playSlideWhistleOnly,
];

/**
 * Custom hook that provides a random funny celebration sound using the Web Audio API.
 * Randomly picks one of: slide whistle + boing, whoopee cushion, cartoon boing, or slide whistle.
 * @returns {{play: () => void}} Object with play method that triggers a random celebration sound
 */
/**
 * Get or create a shared AudioContext.
 * Must be called during a user gesture (click/keydown) to unlock audio.
 * @returns {AudioContext|null}
 */
function getAudioContext() {
  if (typeof window === 'undefined') return null;

  var AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  if (
    !getAudioContext._instance ||
    getAudioContext._instance.state === 'closed'
  ) {
    getAudioContext._instance = new AudioContextClass();
  }

  return getAudioContext._instance;
}
getAudioContext._instance = null;

function useCelebrationSound() {
  var isUnlockedRef = useRef(false);

  // Warm up AudioContext on first user interaction so it's unlocked
  // when the celebration fires later (from a useEffect / Redux change)
  useEffect(function () {
    if (typeof window === 'undefined') return;

    function unlockAudio() {
      if (isUnlockedRef.current) return;

      var ctx = getAudioContext();
      if (!ctx) return;

      // Resume if suspended — this call is allowed because we're
      // inside a user-gesture event handler
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      // Play a silent buffer to fully unlock on iOS/Safari
      var buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      var source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);

      isUnlockedRef.current = true;

      // Remove listeners immediately after unlock
      document.removeEventListener('click', unlockAudio, { capture: true });
      document.removeEventListener('keydown', unlockAudio, { capture: true });
    }

    document.addEventListener('click', unlockAudio, { capture: true });
    document.addEventListener('keydown', unlockAudio, { capture: true });

    return function () {
      document.removeEventListener('click', unlockAudio, { capture: true });
      document.removeEventListener('keydown', unlockAudio, { capture: true });
    };
  }, []);

  var play = useCallback(async function () {
    try {
      var ctx = getAudioContext();
      if (!ctx) return;

      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      var masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(MASTER_VOLUME, ctx.currentTime);
      masterGain.connect(ctx.destination);

      // Pick a random sound
      var index = Math.floor(Math.random() * SOUND_FUNCTIONS.length);
      SOUND_FUNCTIONS[index](ctx, masterGain);

      // Disconnect after longest sound duration (whoopee cushion is 1.2s)
      setTimeout(function () {
        masterGain.disconnect();
      }, 1500);
    } catch (error) {
      console.warn('Celebration sound failed to play:', error);
    }
  }, []);

  return { play: play };
}

module.exports = useCelebrationSound;
